import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Home,
  Info,
  Briefcase,
  FolderCheck,
  PhoneCall,
  Megaphone,
  Save, 
  Plus, 
  Trash2, 
  Check, 
  RefreshCw,
  Layers,
  Sparkles
} from 'lucide-react';
import { useCMS } from '../../../context/CMSContext';

const ManageCMS = () => {
  const { refreshCMS } = useCMS();
  const [cmsData, setCmsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [savingSection, setSavingSection] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Fetch all CMS content
  const fetchCMSData = async () => {
    setLoading(true);
    try {
      const baseUrl = window.API_URL || 'http://localhost:8000';
      const res = await axios.get(`${baseUrl}/cms`);
      setCmsData(res.data);
    } catch (err) {
      console.error('Failed to fetch CMS data:', err);
      setMessage({ type: 'error', text: 'Failed to load CMS content from server.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCMSData();
  }, []);

  const handleSaveSection = async (sectionKey, sectionContent) => {
    setSavingSection(true);
    setMessage({ type: '', text: '' });
    try {
      const baseUrl = window.API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('portalToken') || localStorage.getItem('adminToken') || localStorage.getItem('admin');
      const res = await axios.put(
        `${baseUrl}/cms/${sectionKey}`,
        sectionContent,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCmsData(prev => ({ ...prev, [sectionKey]: res.data.data || sectionContent }));
      setMessage({ type: 'success', text: `Section '${sectionKey.replace('_', ' ')}' saved and updated on website live!` });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      
      // Trigger global context refresh
      if (typeof refreshCMS === 'function') {
        refreshCMS();
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save updates.' });
    } finally {
      setSavingSection(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-slate-600">
        <RefreshCw className="w-8 h-8 animate-spin text-[#f06600] mb-3" />
        <p className="font-medium text-sm">Loading Website CMS Manager...</p>
      </div>
    );
  }

  const tabs = [
    { id: 'home', label: 'Home Page', icon: <Home className="w-4 h-4" /> },
    { id: 'about', label: 'About Us', icon: <Info className="w-4 h-4" /> },
    { id: 'services_page', label: 'Services Page', icon: <Briefcase className="w-4 h-4" /> },
    { id: 'portfolio_page', label: 'Work (Portfolio)', icon: <FolderCheck className="w-4 h-4" /> },
    { id: 'contact', label: 'Contact Us', icon: <PhoneCall className="w-4 h-4" /> },
    { id: 'cta', label: 'Global CTA Banner', icon: <Megaphone className="w-4 h-4" /> },
    { id: 'advanced', label: 'Solutions & Tech', icon: <Layers className="w-4 h-4" /> }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#f06600]" /> Website CMS Manager
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage titles, content, milestones, process roadmaps, and contact information across every page of your website.
          </p>
        </div>
        <button
          onClick={fetchCMSData}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs rounded-xl transition-all self-start sm:self-auto cursor-pointer"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh Data
        </button>
      </div>

      {/* Alert Message */}
      {message.text && (
        <div className={`p-4 rounded-xl text-sm font-medium flex items-center gap-2 ${
          message.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? <Check className="w-4 h-4 text-emerald-600" /> : null}
          {message.text}
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto pb-1 no-scrollbar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 font-semibold text-xs rounded-t-xl transition-all whitespace-nowrap border-b-2 cursor-pointer ${
              activeTab === tab.id
                ? 'border-[#f06600] text-[#f06600] bg-white shadow-sm font-bold'
                : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Active Tab Panel */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6">
        {activeTab === 'home' && (
          <HomePageForm 
            data={cmsData?.home || cmsData?.hero || {}} 
            onSave={(val) => handleSaveSection('home', val)} 
            isSaving={savingSection} 
          />
        )}

        {activeTab === 'about' && (
          <AboutPageForm 
            data={cmsData?.about || {}} 
            onSave={(val) => handleSaveSection('about', val)} 
            isSaving={savingSection} 
          />
        )}

        {activeTab === 'services_page' && (
          <ServicesPageForm 
            data={cmsData?.services_page || {}} 
            onSave={(val) => handleSaveSection('services_page', val)} 
            isSaving={savingSection} 
          />
        )}

        {activeTab === 'portfolio_page' && (
          <PortfolioPageForm 
            data={cmsData?.portfolio_page || {}} 
            onSave={(val) => handleSaveSection('portfolio_page', val)} 
            isSaving={savingSection} 
          />
        )}

        {activeTab === 'contact' && (
          <ContactPageForm 
            data={cmsData?.contact || {}} 
            onSave={(val) => handleSaveSection('contact', val)} 
            isSaving={savingSection} 
          />
        )}

        {activeTab === 'cta' && (
          <GlobalCtaForm 
            data={cmsData?.cta || {}} 
            onSave={(val) => handleSaveSection('cta', val)} 
            isSaving={savingSection} 
          />
        )}

        {activeTab === 'advanced' && (
          <AdvancedSolutionsForm 
            data={cmsData || {}} 
            onSaveSection={handleSaveSection} 
            isSaving={savingSection} 
          />
        )}
      </div>
    </div>
  );
};

/* =========================================================================
   1. HOME PAGE CMS FORM
   ========================================================================= */
const HomePageForm = ({ data, onSave, isSaving }) => {
  const [form, setForm] = useState({
    eyebrowBadge: data.eyebrowBadge || "TECHNOLOGY MEETS CREATIVITY",
    titleLine1: data.titleLine1 || "We Build Digital Solutions",
    titleLine2: data.titleLine2 || "That Grow Your Business",
    description: data.description || "From high-performance websites to intelligent AI systems and digital marketing, we create solutions that deliver real results.",
    primaryBtnText: data.primaryBtnText || "Explore Our Work",
    primaryBtnLink: data.primaryBtnLink || "/portfolio",
    secondaryBtnText: data.secondaryBtnText || "Start a Project",
    secondaryBtnLink: data.secondaryBtnLink || "/contactus",
    scores: Array.isArray(data.scores) ? data.scores : [
      { number: "100+", label: "Happy Clients" },
      { number: "250+", label: "Projects Completed" },
      { number: "5+", label: "Years Experience" },
      { number: "99%", label: "Satisfaction Rate" }
    ]
  });

  const handleScoreChange = (idx, field, value) => {
    const updated = [...form.scores];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, scores: updated });
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-3">
        <h3 className="text-lg font-bold text-slate-900">Home Page Settings</h3>
        <p className="text-xs text-slate-500">Edit the hero banner headline, description, action buttons, and metric numbers.</p>
      </div>

      {/* Hero Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Eyebrow Tagline</label>
          <input
            type="text"
            value={form.eyebrowBadge}
            onChange={(e) => setForm({ ...form, eyebrowBadge: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-[#f06600] outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Headline Line 1</label>
          <input
            type="text"
            value={form.titleLine1}
            onChange={(e) => setForm({ ...form, titleLine1: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-[#f06600] outline-none"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Headline Line 2 (Highlighted)</label>
          <input
            type="text"
            value={form.titleLine2}
            onChange={(e) => setForm({ ...form, titleLine2: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-[#f06600] outline-none"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Subtitle / Description</label>
          <textarea
            rows="3"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-[#f06600] outline-none"
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Primary Button Text</label>
          <input
            type="text"
            value={form.primaryBtnText}
            onChange={(e) => setForm({ ...form, primaryBtnText: e.target.value })}
            className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Primary Button Link</label>
          <input
            type="text"
            value={form.primaryBtnLink}
            onChange={(e) => setForm({ ...form, primaryBtnLink: e.target.value })}
            className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Secondary Button Text</label>
          <input
            type="text"
            value={form.secondaryBtnText}
            onChange={(e) => setForm({ ...form, secondaryBtnText: e.target.value })}
            className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Secondary Button Link</label>
          <input
            type="text"
            value={form.secondaryBtnLink}
            onChange={(e) => setForm({ ...form, secondaryBtnLink: e.target.value })}
            className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
          />
        </div>
      </div>

      {/* Metric Scores */}
      <div className="pt-4 border-t border-slate-200">
        <h4 className="text-xs font-bold text-[#f06600] uppercase tracking-wider mb-3">Key Metric Numbers</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {form.scores.map((s, idx) => (
            <div key={idx} className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase">Metric Value</label>
                <input
                  type="text"
                  value={s.number || s.score || ''}
                  onChange={(e) => handleScoreChange(idx, 'number', e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-sm font-bold text-slate-900"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 uppercase">Metric Label</label>
                <input
                  type="text"
                  value={s.label || s.title || ''}
                  onChange={(e) => handleScoreChange(idx, 'label', e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-200">
        <button
          onClick={() => onSave(form)}
          disabled={isSaving}
          className="flex items-center gap-2 bg-[#f06600] hover:bg-[#d05800] text-white font-bold px-6 py-2.5 rounded-xl shadow transition-all text-xs cursor-pointer"
        >
          <Save className="w-4 h-4" /> Save Home Page Content
        </button>
      </div>
    </div>
  );
};

/* =========================================================================
   2. ABOUT US PAGE CMS FORM
   ========================================================================= */
const AboutPageForm = ({ data, onSave, isSaving }) => {
  const [form, setForm] = useState({
    heroEyebrow: data.heroEyebrow || "ABOUT US",
    heroTitle: data.heroTitle || "A Team Behind Your Digital Growth",
    heroSubtitle: data.heroSubtitle || "We are creators, developers, marketers and AI enthusiasts on a mission to help businesses grow in the digital world.",
    missionTitle: data.missionTitle || "Our Mission",
    missionDesc: data.missionDesc || "To empower businesses with innovative digital solutions, combining creativity, technology and strategy to create meaningful growth and real-world impact.",
    visionTitle: data.visionTitle || "Our Vision",
    visionDesc: data.visionDesc || "To be a global digital community where businesses, ideas and people grow together — leveraging technology and AI to build a smarter, more opportunity-driven world.",
    milestones: Array.isArray(data.milestones) ? data.milestones : [
      { year: "2020", title: "The Beginning", desc: "Turned our vision into action." },
      { year: "2021", title: "First Clients", desc: "Delivered our first successful projects." },
      { year: "2022", title: "Team Expansion", desc: "Welcomed a growing team of experts." },
      { year: "2023", title: "Service Growth", desc: "Expanded into AI & digital marketing." },
      { year: "2024", title: "Global Reach", desc: "Started working with international clients." },
      { year: "2025", title: "Bigger Impact", desc: "Continuing to build a better digital tomorrow." }
    ]
  });

  const handleMilestoneChange = (idx, field, value) => {
    const updated = [...form.milestones];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, milestones: updated });
  };

  const addMilestone = () => {
    setForm({
      ...form,
      milestones: [
        ...form.milestones,
        { year: "2026", title: "Next Level", desc: "Expanding operations and new products." }
      ]
    });
  };

  const removeMilestone = (idx) => {
    setForm({
      ...form,
      milestones: form.milestones.filter((_, i) => i !== idx)
    });
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-3">
        <h3 className="text-lg font-bold text-slate-900">About Us Page Settings</h3>
        <p className="text-xs text-slate-500">Edit hero headline, mission, vision, and timeline milestones.</p>
      </div>

      {/* Hero */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Eyebrow</label>
          <input
            type="text"
            value={form.heroEyebrow}
            onChange={(e) => setForm({ ...form, heroEyebrow: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Headline</label>
          <input
            type="text"
            value={form.heroTitle}
            onChange={(e) => setForm({ ...form, heroTitle: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Subtitle</label>
          <textarea
            rows="2"
            value={form.heroSubtitle}
            onChange={(e) => setForm({ ...form, heroSubtitle: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="pt-4 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Mission Title</label>
          <input
            type="text"
            value={form.missionTitle}
            onChange={(e) => setForm({ ...form, missionTitle: e.target.value })}
            className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
          />
          <label className="block text-xs font-bold text-slate-700 uppercase mt-2 mb-1">Mission Description</label>
          <textarea
            rows="3"
            value={form.missionDesc}
            onChange={(e) => setForm({ ...form, missionDesc: e.target.value })}
            className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Vision Title</label>
          <input
            type="text"
            value={form.visionTitle}
            onChange={(e) => setForm({ ...form, visionTitle: e.target.value })}
            className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
          />
          <label className="block text-xs font-bold text-slate-700 uppercase mt-2 mb-1">Vision Description</label>
          <textarea
            rows="3"
            value={form.visionDesc}
            onChange={(e) => setForm({ ...form, visionDesc: e.target.value })}
            className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
          />
        </div>
      </div>

      {/* Milestones */}
      <div className="pt-4 border-t border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold text-[#f06600] uppercase tracking-wider">Our Journey / Milestones</h4>
          <button
            onClick={addMilestone}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-[#f06600] font-bold text-xs rounded-lg border border-orange-200 cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" /> Add Milestone
          </button>
        </div>

        <div className="space-y-3">
          {form.milestones.map((m, idx) => (
            <div key={idx} className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
              <div className="w-20 shrink-0">
                <label className="block text-[10px] font-semibold text-slate-500 uppercase">Year</label>
                <input
                  type="text"
                  value={m.year}
                  onChange={(e) => handleMilestoneChange(idx, 'year', e.target.value)}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded-lg text-sm font-bold text-[#f06600]"
                />
              </div>
              <div className="flex-1 space-y-1.5">
                <input
                  type="text"
                  value={m.title}
                  placeholder="Milestone Title"
                  onChange={(e) => handleMilestoneChange(idx, 'title', e.target.value)}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-900"
                />
                <input
                  type="text"
                  value={m.desc}
                  placeholder="Milestone Description"
                  onChange={(e) => handleMilestoneChange(idx, 'desc', e.target.value)}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded-lg text-xs text-slate-600"
                />
              </div>
              <button
                onClick={() => removeMilestone(idx)}
                className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors mt-4 cursor-pointer"
                title="Delete milestone"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-200">
        <button
          onClick={() => onSave(form)}
          disabled={isSaving}
          className="flex items-center gap-2 bg-[#f06600] hover:bg-[#d05800] text-white font-bold px-6 py-2.5 rounded-xl shadow transition-all text-xs cursor-pointer"
        >
          <Save className="w-4 h-4" /> Save About Us Content
        </button>
      </div>
    </div>
  );
};

/* =========================================================================
   3. SERVICES PAGE CMS FORM
   ========================================================================= */
const ServicesPageForm = ({ data, onSave, isSaving }) => {
  const [form, setForm] = useState({
    heroEyebrow: data.heroEyebrow || "OUR SERVICES",
    heroTitle: data.heroTitle || "Services That Drive Real Growth",
    heroSubtitle: data.heroSubtitle || "From design to development to marketing, we build modern digital solutions that help your business succeed.",
    processHeading: data.processHeading || "A Simple Process for Amazing Results",
    processSubtitle: data.processSubtitle || "We follow a clear and collaborative process to turn your ideas into successful digital products.",
    processSteps: Array.isArray(data.processSteps) ? data.processSteps : [
      { num: "01", title: "Discover", desc: "Understand your goals, target audience, and business challenges." },
      { num: "02", title: "Plan", desc: "Strategy, technical architecture, and milestone roadmap tailored to your needs." },
      { num: "03", title: "Design", desc: "High-converting UI/UX designs, design systems, and interactive prototypes." },
      { num: "04", title: "Develop", desc: "Clean, robust, and scalable full-stack development with rigorous QA." },
      { num: "05", title: "Launch", desc: "Cloud deployment, performance optimization, and continuous growth support." }
    ]
  });

  const handleStepChange = (idx, field, value) => {
    const updated = [...form.processSteps];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, processSteps: updated });
  };

  const addStep = () => {
    const nextNum = String(form.processSteps.length + 1).padStart(2, '0');
    setForm({
      ...form,
      processSteps: [
        ...form.processSteps,
        { num: nextNum, title: "Optimize", desc: "Continuous iteration and performance tuning." }
      ]
    });
  };

  const removeStep = (idx) => {
    setForm({
      ...form,
      processSteps: form.processSteps.filter((_, i) => i !== idx)
    });
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-3">
        <h3 className="text-lg font-bold text-slate-900">Services Page Settings</h3>
        <p className="text-xs text-slate-500">Edit hero titles and the 5-step process roadmap.</p>
      </div>

      {/* Hero */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Eyebrow</label>
          <input
            type="text"
            value={form.heroEyebrow}
            onChange={(e) => setForm({ ...form, heroEyebrow: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Headline</label>
          <input
            type="text"
            value={form.heroTitle}
            onChange={(e) => setForm({ ...form, heroTitle: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Subtitle</label>
          <textarea
            rows="2"
            value={form.heroSubtitle}
            onChange={(e) => setForm({ ...form, heroSubtitle: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
      </div>

      {/* Process Section */}
      <div className="pt-4 border-t border-slate-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Process Section Heading</label>
            <input
              type="text"
              value={form.processHeading}
              onChange={(e) => setForm({ ...form, processHeading: e.target.value })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Process Section Subtitle</label>
            <input
              type="text"
              value={form.processSubtitle}
              onChange={(e) => setForm({ ...form, processSubtitle: e.target.value })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <h4 className="text-xs font-bold text-[#f06600] uppercase tracking-wider">Process Steps Roadmap</h4>
          <button
            onClick={addStep}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-[#f06600] font-bold text-xs rounded-lg border border-orange-200 cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" /> Add Process Step
          </button>
        </div>

        <div className="space-y-3">
          {form.processSteps.map((step, idx) => (
            <div key={idx} className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
              <div className="w-16 shrink-0">
                <label className="block text-[10px] font-semibold text-slate-500 uppercase">Step</label>
                <input
                  type="text"
                  value={step.num || step.step || ''}
                  onChange={(e) => handleStepChange(idx, 'num', e.target.value)}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded-lg text-sm font-black text-[#f06600] text-center"
                />
              </div>
              <div className="flex-1 space-y-1.5">
                <input
                  type="text"
                  value={step.title}
                  placeholder="Step Title"
                  onChange={(e) => handleStepChange(idx, 'title', e.target.value)}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-900"
                />
                <input
                  type="text"
                  value={step.desc}
                  placeholder="Step Description"
                  onChange={(e) => handleStepChange(idx, 'desc', e.target.value)}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded-lg text-xs text-slate-600"
                />
              </div>
              <button
                onClick={() => removeStep(idx)}
                className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors mt-3 cursor-pointer"
                title="Delete step"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-200">
        <button
          onClick={() => onSave(form)}
          disabled={isSaving}
          className="flex items-center gap-2 bg-[#f06600] hover:bg-[#d05800] text-white font-bold px-6 py-2.5 rounded-xl shadow transition-all text-xs cursor-pointer"
        >
          <Save className="w-4 h-4" /> Save Services Page Content
        </button>
      </div>
    </div>
  );
};

/* =========================================================================
   4. WORK (PORTFOLIO) PAGE CMS FORM
   ========================================================================= */
const PortfolioPageForm = ({ data, onSave, isSaving }) => {
  const [form, setForm] = useState({
    heroEyebrow: data.heroEyebrow || "OUR WORK",
    heroTitle: data.heroTitle || "Projects That Speak for Themselves",
    heroSubtitle: data.heroSubtitle || "Explore our portfolio of websites, web applications, AI solutions, and digital growth campaigns built for ambitious brands.",
    processHeading: data.processHeading || "How We Turn Ideas Into Successful Projects",
    processSubtitle: data.processSubtitle || "We follow a proven, collaborative process to ensure every project is delivered on time, meets your goals, and creates real impact.",
    processSteps: Array.isArray(data.processSteps) ? data.processSteps : [
      { num: "01", title: "Discover", desc: "We understand your goals, audience, and challenges." },
      { num: "02", title: "Plan", desc: "We create a strategy and roadmap tailored to your needs." },
      { num: "03", title: "Design", desc: "Our team designs modern, user-focused experiences." },
      { num: "04", title: "Develop", desc: "We build with the latest technologies and best practices." },
      { num: "05", title: "Launch", desc: "We test, deploy and ensure a smooth launch." },
      { num: "06", title: "Grow", desc: "We provide ongoing support and strategies for continuous growth." }
    ]
  });

  const handleStepChange = (idx, field, value) => {
    const updated = [...form.processSteps];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, processSteps: updated });
  };

  const addStep = () => {
    const nextNum = String(form.processSteps.length + 1).padStart(2, '0');
    setForm({
      ...form,
      processSteps: [
        ...form.processSteps,
        { num: nextNum, title: "Scale", desc: "Expand reach and grow revenue." }
      ]
    });
  };

  const removeStep = (idx) => {
    setForm({
      ...form,
      processSteps: form.processSteps.filter((_, i) => i !== idx)
    });
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-3">
        <h3 className="text-lg font-bold text-slate-900">Work (Portfolio) Page Settings</h3>
        <p className="text-xs text-slate-500">Edit hero headline, work intro, and the 6-step project workflow.</p>
      </div>

      {/* Hero */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Eyebrow</label>
          <input
            type="text"
            value={form.heroEyebrow}
            onChange={(e) => setForm({ ...form, heroEyebrow: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Headline</label>
          <input
            type="text"
            value={form.heroTitle}
            onChange={(e) => setForm({ ...form, heroTitle: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Subtitle</label>
          <textarea
            rows="2"
            value={form.heroSubtitle}
            onChange={(e) => setForm({ ...form, heroSubtitle: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
      </div>

      {/* Process Section */}
      <div className="pt-4 border-t border-slate-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Process Section Heading</label>
            <input
              type="text"
              value={form.processHeading}
              onChange={(e) => setForm({ ...form, processHeading: e.target.value })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Process Section Subtitle</label>
            <input
              type="text"
              value={form.processSubtitle}
              onChange={(e) => setForm({ ...form, processSubtitle: e.target.value })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <h4 className="text-xs font-bold text-[#f06600] uppercase tracking-wider">Workflow Steps</h4>
          <button
            onClick={addStep}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-[#f06600] font-bold text-xs rounded-lg border border-orange-200 cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" /> Add Step
          </button>
        </div>

        <div className="space-y-3">
          {form.processSteps.map((step, idx) => (
            <div key={idx} className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
              <div className="w-16 shrink-0">
                <label className="block text-[10px] font-semibold text-slate-500 uppercase">Step</label>
                <input
                  type="text"
                  value={step.num}
                  onChange={(e) => handleStepChange(idx, 'num', e.target.value)}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded-lg text-sm font-black text-[#f06600] text-center"
                />
              </div>
              <div className="flex-1 space-y-1.5">
                <input
                  type="text"
                  value={step.title}
                  placeholder="Step Title"
                  onChange={(e) => handleStepChange(idx, 'title', e.target.value)}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-900"
                />
                <input
                  type="text"
                  value={step.desc}
                  placeholder="Step Description"
                  onChange={(e) => handleStepChange(idx, 'desc', e.target.value)}
                  className="w-full px-2 py-1 bg-white border border-slate-300 rounded-lg text-xs text-slate-600"
                />
              </div>
              <button
                onClick={() => removeStep(idx)}
                className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors mt-3 cursor-pointer"
                title="Delete step"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-200">
        <button
          onClick={() => onSave(form)}
          disabled={isSaving}
          className="flex items-center gap-2 bg-[#f06600] hover:bg-[#d05800] text-white font-bold px-6 py-2.5 rounded-xl shadow transition-all text-xs cursor-pointer"
        >
          <Save className="w-4 h-4" /> Save Work Page Content
        </button>
      </div>
    </div>
  );
};

/* =========================================================================
   5. CONTACT US PAGE CMS FORM
   ========================================================================= */
const ContactPageForm = ({ data, onSave, isSaving }) => {
  const [form, setForm] = useState({
    heroEyebrow: data.heroEyebrow || "GET IN TOUCH",
    heroTitle: data.heroTitle || "Let's Build Something Great Together",
    heroSubtitle: data.heroSubtitle || "Have a project in mind, questions about our services, or want to discuss how we can help your business grow? We'd love to hear from you.",
    phone: data.phone || "+92 300 1234567",
    email: data.email || "contact@betacommunes.com",
    address: data.address || "Karachi, Pakistan / Remote Worldwide",
    workingHours: data.workingHours || "Monday - Saturday: 9:00 AM - 6:00 PM PKT",
    socialLinks: data.socialLinks || {
      linkedin: "https://linkedin.com",
      twitter: "https://twitter.com",
      facebook: "https://facebook.com",
      instagram: "https://instagram.com"
    }
  });

  return (
    <div className="space-y-6">
      <div className="border-b pb-3">
        <h3 className="text-lg font-bold text-slate-900">Contact Us Page Settings</h3>
        <p className="text-xs text-slate-500">Edit hero headlines, direct phone numbers, email, addresses, and social channels.</p>
      </div>

      {/* Hero */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Eyebrow</label>
          <input
            type="text"
            value={form.heroEyebrow}
            onChange={(e) => setForm({ ...form, heroEyebrow: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Headline</label>
          <input
            type="text"
            value={form.heroTitle}
            onChange={(e) => setForm({ ...form, heroTitle: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Hero Subtitle</label>
          <textarea
            rows="2"
            value={form.heroSubtitle}
            onChange={(e) => setForm({ ...form, heroSubtitle: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
      </div>

      {/* Contact Channels */}
      <div className="pt-4 border-t border-slate-200">
        <h4 className="text-xs font-bold text-[#f06600] uppercase tracking-wider mb-3">Direct Contact Details</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Phone Number</label>
            <input
              type="text"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Official Email</label>
            <input
              type="text"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Office / Location Address</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Working Hours / Schedule</label>
            <input
              type="text"
              value={form.workingHours}
              onChange={(e) => setForm({ ...form, workingHours: e.target.value })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
        </div>
      </div>

      {/* Social Handles */}
      <div className="pt-4 border-t border-slate-200">
        <h4 className="text-xs font-bold text-[#f06600] uppercase tracking-wider mb-3">Social Media URLs</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">LinkedIn URL</label>
            <input
              type="text"
              value={form.socialLinks?.linkedin || ''}
              onChange={(e) => setForm({
                ...form,
                socialLinks: { ...form.socialLinks, linkedin: e.target.value }
              })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Twitter / X URL</label>
            <input
              type="text"
              value={form.socialLinks?.twitter || ''}
              onChange={(e) => setForm({
                ...form,
                socialLinks: { ...form.socialLinks, twitter: e.target.value }
              })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Instagram URL</label>
            <input
              type="text"
              value={form.socialLinks?.instagram || ''}
              onChange={(e) => setForm({
                ...form,
                socialLinks: { ...form.socialLinks, instagram: e.target.value }
              })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Facebook URL</label>
            <input
              type="text"
              value={form.socialLinks?.facebook || ''}
              onChange={(e) => setForm({
                ...form,
                socialLinks: { ...form.socialLinks, facebook: e.target.value }
              })}
              className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-200">
        <button
          onClick={() => onSave(form)}
          disabled={isSaving}
          className="flex items-center gap-2 bg-[#f06600] hover:bg-[#d05800] text-white font-bold px-6 py-2.5 rounded-xl shadow transition-all text-xs cursor-pointer"
        >
          <Save className="w-4 h-4" /> Save Contact Us Content
        </button>
      </div>
    </div>
  );
};

/* =========================================================================
   6. GLOBAL CTA BANNER FORM
   ========================================================================= */
const GlobalCtaForm = ({ data, onSave, isSaving }) => {
  const [form, setForm] = useState({
    badge: data.badge || "READY TO SCALE?",
    heading: data.heading || "Ready to Grow Your Business Digitally?",
    description: data.description || "Partner with Beta Communes to build high-performance web applications, scalable ERPs, and smart AI automations.",
    primaryBtnText: data.primaryBtnText || "Start Your Project",
    primaryBtnLink: data.primaryBtnLink || "/contactus"
  });

  return (
    <div className="space-y-6">
      <div className="border-b pb-3">
        <h3 className="text-lg font-bold text-slate-900">Global Call To Action Banner</h3>
        <p className="text-xs text-slate-500">Edit the bottom call-to-action banner displayed across the website.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Eyebrow Tag / Badge</label>
          <input
            type="text"
            value={form.badge}
            onChange={(e) => setForm({ ...form, badge: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Banner Headline</label>
          <input
            type="text"
            value={form.heading}
            onChange={(e) => setForm({ ...form, heading: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Description</label>
          <textarea
            rows="2"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Button Text</label>
          <input
            type="text"
            value={form.primaryBtnText}
            onChange={(e) => setForm({ ...form, primaryBtnText: e.target.value })}
            className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Button Link</label>
          <input
            type="text"
            value={form.primaryBtnLink}
            onChange={(e) => setForm({ ...form, primaryBtnLink: e.target.value })}
            className="w-full px-3.5 py-2 border border-slate-300 rounded-xl text-sm"
          />
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-200">
        <button
          onClick={() => onSave(form)}
          disabled={isSaving}
          className="flex items-center gap-2 bg-[#f06600] hover:bg-[#d05800] text-white font-bold px-6 py-2.5 rounded-xl shadow transition-all text-xs cursor-pointer"
        >
          <Save className="w-4 h-4" /> Save Global CTA Banner
        </button>
      </div>
    </div>
  );
};

/* =========================================================================
   7. ADVANCED SOLUTIONS & TECH STACK FORM
   ========================================================================= */
const AdvancedSolutionsForm = ({ data, onSaveSection, isSaving }) => {
  const [whyUs, setWhyUs] = useState(data.why_us || {
    heading: "Why Work With Beta Communes?",
    subheading: "We bridge cutting-edge enterprise technology with agile delivery to empower your digital success."
  });

  return (
    <div className="space-y-6">
      <div className="border-b pb-3">
        <h3 className="text-lg font-bold text-slate-900">Advanced Capabilities & Value Propositions</h3>
        <p className="text-xs text-slate-500">Configure why choose us and custom enterprise solutions.</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Why Choose Us Headline</label>
          <input
            type="text"
            value={whyUs.heading}
            onChange={(e) => setWhyUs({ ...whyUs, heading: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Why Choose Us Subtitle</label>
          <textarea
            rows="2"
            value={whyUs.subheading}
            onChange={(e) => setWhyUs({ ...whyUs, subheading: e.target.value })}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm"
          />
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-200">
        <button
          onClick={() => onSaveSection('why_us', whyUs)}
          disabled={isSaving}
          className="flex items-center gap-2 bg-[#f06600] hover:bg-[#d05800] text-white font-bold px-6 py-2.5 rounded-xl shadow transition-all text-xs cursor-pointer"
        >
          <Save className="w-4 h-4" /> Save Advanced Settings
        </button>
      </div>
    </div>
  );
};

export default ManageCMS;
